import React from "react";
import { html } from "lit-html";
import * as XLSX from 'xlsx';
import parse from 'html-react-parser';

// Shared components and utilities
import { 
    OmniIconComponent, 
    OmniSearchComponent, 
    OmniTableStickyComponent, 
    OmniTileComponent, 
    OmniToolbarComponent, 
    OmniIconMenu 
} from "../../../../shared/components/omni-components";
import { LoaderComponent, AlertMessagesService, Utils } from '../../../../shared';

// Feature imports
import { VendorMappingService } from '../../';


export class VendorMappingComponent extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            filtersApplied: false,
            formData: [],
            dropdownOptions: {},
            spinnerCount: 0,
            disableForm: true,
            disableCancel: true,
            isAlertOpen: false,
            loaded: false
        }
    }

    responseData = [];
    deleteData = {};

    // Lifecycle methods
    componentDidMount() {
        this.getVendorMappingList();
    }

    /**
     * the below code fetches the vendor mapping list and dropdown options
     * and set the dropdown options and vendor mapping list in the state
     * @function getVendorMappingList
     * @returns
    */
    getVendorMappingList = () => {
        const dropDownOptions = VendorMappingService.getDropdownOptions();
        const mappimngData = VendorMappingService.getMappings();
        Promise.all([dropDownOptions, mappimngData]).then((values) => {
            this.responseData = values[1].data;
            this.setState({
                dropdownOptions: this.createDropdownOptions(values[0].data), 
                formData: values[1].data,
                loaded: true,
                disableForm: true,
                disableCancel: true
            }, () => {
                if(this.state.formData.length > 0){
                    this.addFileUploadEventHandler();
                    setTimeout(() => {
                        let tableContainer = document.getElementById('vendorMappingTable').shadowRoot.querySelector('.scroll-container');
                        tableContainer.style.minHeight = '65vh';
                    }, 0);
                }
            });
        })
        .catch((error) => {
            AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Refresh', retryBtnEvent: this.getVendorMappingList, title: 'Unable to load the data', bodyMsgArr: []}]);
        })
        .finally(() => {
            this.props.setSpinnerCount(0);
        });
    }


    /**
     * The below code creates the dropdown options for the vendor mapping form
     * @function createDropdownOptions
     * @param {array} data - dropdown options data from the API
     * @returns {object}
    */ 
    createDropdownOptions = (data) => {
        let dropdownOptions = {};
        data.forEach((row) => {
            dropdownOptions[row.name + 'Options'] = row.values.map((option) => {
                return {
                    value: option,
                    label: option
                }
            });
        });
        return dropdownOptions;
    }

    /**
     * The below code return the import and export options
     * @returns {array} - import and export options
    */
    importExportOption = () => {
        return ["Import data", "Export data"];
    }

    /**
     * The below code adds the new row to the vendor mapping form and set the state
     * @function getFormFields
     * @returns
    */ 
    addMappingRow = () => {
        let formadata = JSON.parse(JSON.stringify(this.state.formData));
        formadata.unshift({
            vendorName: '',
            planIt: '',
            mediaTools: '',
            mediaOcean: '',
            omniDictionary: '',
            startDate: '',
            endDate: ''
        });
        this.setState({formData: formadata, disableCancel: false}, () => {
                setTimeout(() => {
                    let tableContainer = document.getElementById('vendorMappingTable').shadowRoot.querySelector('.scroll-container');
                    tableContainer.style.minHeight = '65vh';
                }, 0);
        });
    }

    /**
     * The below code triggered when the user clicks on the Import/Export button
     * Based on the label it will trigger the import or export functionality
     * @function importExportClick
     * @param {object} event - event object
     * @returns
    */ 
    importExportClick = (event) => {
        let clientId = decodeURIComponent(this.props.params.clientId);
        const label = event?.detail?.value?.label;
        if (label.includes('Export')) {
            let outputData = [];
            for(let i=0;i<this.state.formData.length;i++){
                let row = this.state.formData[i];
                outputData.push({
                    'Vendor Name': row.vendorName,
                    'PlanIt': row.planIt,
                    'Media Tools': row.mediaTools,
                    'Media Ocean': row.mediaOcean,
                    'Omni Dictionary': row.omniDictionary,
                    'Start Date': row.startDate,
                    'End Date': row.endDate
                });
            }
            Utils.exportToExcel(outputData,"Vendor Mapping",`${clientId}_VendorMapping_List.xlsx`);
        } else if (label.includes('Import')) {
            document.getElementById('vendorMappingInput').click();
        }
    }

    /**
     * Adds a file upload event handler to a file input element with the ID 'vendorMappingInput'.
     * When a file is selected, this function reads the file's contents, processes it as an Excel spreadsheet,
     * and imports the data into the system as rate and fee information.
     * @function addFileUploadEventHandler
     * @returns
    */ 
    addFileUploadEventHandler = () => {
        const fileInput = document.getElementById(`vendorMappingInput`);
    
        fileInput.addEventListener("click", (event) => {
          event.stopImmediatePropagation();
          fileInput.value = null;
        });
    
        fileInput.addEventListener("change", (event) => {
            event.stopImmediatePropagation();
            const file = fileInput.files[0];
            const reader = new FileReader();
            reader.onload = (event) => {
                const data = new Uint8Array(event.target.result);
                const workbook = XLSX.read(data, {
                    type: "array",
                    cellDates: true,
                    dateNF: "yyyy-mm-dd",
                });
                let payload = [];
                for (let sheetName of workbook.SheetNames) {
                    let worksheet = workbook.Sheets[sheetName];
                    let sheetRows = XLSX.utils.sheet_to_json(worksheet, {raw: false, blankrows: false});
                    for(let i=0;i<sheetRows.length;i++){
                        payload.push({
                            vendorName: sheetRows[i]['Vendor Name'],
                            planIt: sheetRows[i]['PlanIt'],
                            mediaTools: sheetRows[i]['Media Tools'],
                            mediaOcean: sheetRows[i]['Media Ocean'],
                            omniDictionary: sheetRows[i]['Omni Dictionary'],
                            startDate: sheetRows[i]['Start Date'].split(' ')[0],
                            endDate: sheetRows[i]['End Date'].split(' ')[0]
                        })
                    }
                }
                this.props.setSpinnerCount(1);
                VendorMappingService.addMappings(payload).then((response) => {
                    if (response.data) {
                        this.getVendorMappingList();
                        AlertMessagesService.showSuccessToastMessage('Vendor mappings added successfully');
                    }
                })
                .catch((error) => {
                    AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.addFileUploadEventHandler, title: 'Unable to add the vendor mappings', bodyMsgArr: []}]);
                })
                .finally(() => {
                    this.props.setSpinnerCount(0);
                });
            }
            reader.readAsArrayBuffer(file);
        });
    };

    /**
     * The below code triggered when the user searches for the vendor mapping
     * Based on the search text it will call the filter API and set the state
     * @function updateSearch
     * @param {object} event - event object
     * @returns
    */ 
    updateSearch = (event) => {
    }

    /**
     * The below code triggered when the user clicks on the delete button
     * It will set the state with the delete data and open the alert box
     * @function deleteRow
     * @param {object} value - delete data
     * @param {number} index - delete index
     * @returns
    */ 
    deleteRow = (value, index) => {
        this.deleteData = value;
        this.setState({ isAlertOpen: true});
    }

    /**
     * The below code triggered when the user clicks on the confirm delete button
     * It will call the delete API and set the state with the updated data
     * @function confirmDelete
     * @returns
    */ 
    confirmDelete = () => {
        let deletePayload= this.deleteData;
        if(deletePayload.vendorMappingId){
            this.setState({ isAlertOpen: false, spinnerCount: 1 });
            VendorMappingService.deleteVendorMapping(deletePayload)
            .then((response) => {
                this.getVendorMappingList();
                AlertMessagesService.showSuccessToastMessage('Selected Vendor mapping is deleted!');
            })
            .catch((error) => {
                AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.confirmDelete, title: 'Unable to delete the selected vendor mapping', bodyMsgArr: []}]);
            })
            .finally(() => {
                this.setState({ spinnerCount: 0});
            });
        }
    }

    /**
     * The below code triggered when the user clicks on the save button
     * It will refactore the data either to new or edit and call the add/update API based on the data and set the state with the updated data
     * @function saveEdits
     * @returns
    */ 
    saveEdits = () => {
        let editData = [];
        let newData = [];
        this.state.formData.forEach((row) => {
            row.startDate = row.startDate?.split(' ')[0];
            row.endDate = row.endDate?.split(' ')[0];
            if(row.vendorMappingId && row.isEdit){
                editData.push(row);
            } else if(!row.vendorMappingId && row.isEdit){
                newData.push(row);
            }
        });
        if(editData.length > 0){
            this.props.setSpinnerCount(1);
            VendorMappingService.updateMappings(editData).then((response) => {
                if (response.data) {
                    this.getVendorMappingList();
                    AlertMessagesService.showSuccessToastMessage('Vendor mappings updated successfully');
                }
            })
            .catch((error) => {
                AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.saveEdits, title: 'Unable to update the vendor mappings', bodyMsgArr: []}]);
            })
            .finally(() => {
                this.props.setSpinnerCount(0);
            });
        }
        if(newData.length > 0){
            this.props.setSpinnerCount(1);
            VendorMappingService.addMappings(newData).then((response) => {
                if (response.data) {
                    this.getVendorMappingList();
                    AlertMessagesService.showSuccessToastMessage('Vendor mappings added successfully');
                }
            })
            .catch((error) => {
                AlertMessagesService.showMultipleAlerts([{type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.saveEdits, title: 'Unable to add the vendor mappings', bodyMsgArr: []}]);
            })
            .finally(() => {
                this.props.setSpinnerCount(0);
            });
        }
    }

    /**
     * The below code triggered when the user clicks on the cancel button
     * It will set the state with the response data and disable the save/cancel button
     * @function cancelEdits
     * @returns
    */ 
    cancelEdits = () => {
        this.setState({formData: this.responseData, disableForm: true, disableCancel: true}, () => {
            if(this.state.formData.length > 0){
                setTimeout(() => {
                    let tableContainer = document.getElementById('vendorMappingTable').shadowRoot.querySelector('.scroll-container');
                    tableContainer.style.minHeight = '65vh';
                }, 0);
            }
        });
    }

    /**
     * The below code is creating a table cell (`<td>`) with a dropdown menu (`<omni-dropdown>`) inside it.
     * The dropdown menu is populated with options from the `value[dropdownTitle + 'Options']` array. The
     * selected value of the dropdown menu is set to `value[dropdownTitle]`. When the dropdown menu value
     * is changed, the `onDropDownChange` function is called with the `value`, `index`, `dropdownTitle`,
     * and `detail` as arguments.
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} dropdownTitle
     * @param {String} placeholder
     * @param {String} dropDownOption 
     * @returns 
     */
     getDropdownTemplate = (value, index, dropdownTitle, placeholder, dropDownOption) => {
        return html`
        <style>
            :host::part(target) {
                padding: 10px !important;
            }
            :host td{
                overflow: revert !important;
            }
        </style>
        <td>
            <omni-dropdown
            style="min-width: 100px; max-width: 180px;"
            typeahead
            searchindropdown
            hidefooter
            searchPlaceholder="Select"
            .placeholder=${placeholder}
            .dynamicadditions=${['vendorName', 'planIt', 'mediaOcean'].includes(dropdownTitle)}
            .value=${this.state.formData[index][dropdownTitle]? [this.state.formData[index][dropdownTitle]] : []}
            .options=${this.state.dropdownOptions[(dropDownOption ? dropDownOption : dropdownTitle) + 'Options']} 
            @change=${({ detail }) => {
                if (typeof detail === 'string') {
                    detail = { value: detail, label: detail };
                }
                this.onFormUpdate(value, index, dropdownTitle, detail)
            }}>
            </omni-dropdown>
        </td>`
    }

    /**
     * The below code has if statement that checks if the `event.value` is truthy (not null or undefined). 
     * If it is truthy, the code will update the values of a dropdown menu in a form. 
     * @param {String} row 
     * @param {Number} index 
     * @param {String} dropdownTitle 
     * @param {Object} event 
     */
     onFormUpdate = (row, index, dropdownTitle, event) => {
        if (event?.value || event?.target?.value) {
            const formData = JSON.parse(JSON.stringify(this.state.formData));
            let isEdit = false;
            formData[index][dropdownTitle] = event?.value || event?.target?.value;
            if(formData[index][dropdownTitle] !== this.state.formData[index][dropdownTitle]){
                isEdit = true;
                formData[index]['isEdit'] = true;
            }
            this.setState({ formData: formData, disableForm: false, disableCancel: false});
        }
    }

    /**
     * The below code is checking if the values in the formData object match the values in the data array
     * for each corresponding key. It iterates over each key in the formData object and then iterates over
     * each index in the data array. If any value in the formData object does not match the corresponding
     * value in the data array, it returns false. If all values match, it returns true.
     * @param {Object} formData 
     * @returns {Boolean}
     */
     isFormUpdated = (index) => {
        const ignoredKeys = ['vendorMappingId', 'isEdit', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'];
        const keys = Object.keys(this.state.formData[index]);
        for (const key of keys) {
            if(!ignoredKeys.includes(key)){
                if(!this.state.formData[index][key]) return;
            }
        }
        this.setState({disableForm: false, disableCancel: false});
    }

    /**
     * The below code is creating a table cell (`<td>`) with date type input field (`<input type="date">`)
     * When a date is selected, the `updateValueInFormdata` function is called with the `value`, `index`,
     * `field`, and the new value as arguments. 
     * @param {Object} value 
     * @param {Number} index 
     * @param {String} field 
     * @returns 
     */
     getDateTemplate = (value, index, field) => {
        return html `
        <style>
        input[type="date"]:before {
            content: attr(data-date);
            margin-left: 35px;
            color: black;
        }

        input[type="date"]::-webkit-datetime-edit,
        input[type="date"]::-webkit-inner-spin-button,
        input[type="date"]::-webkit-clear-button {
            display: none;  
        }

        input[type="date"]::-webkit-calendar-picker-indicator {
            position: absolute;
            top: 6px;
            left: 15px;
            width: 18px;
            height: 18px;
            content: 'asdasd';
            color: black;
            opacity: 1;
        }    
        </style>
        <td id=${index + '-date'}>
            <div className="date-field">
                <input
                id=${field + '-date-' + index}
                data-date=${this.state.formData[index][field] ? Utils.formatDateObject(Array.isArray(this.state.formData[index][field]) ? this.state.formData[index][field][0] : this.state.formData[index][field]) : "Mon DD, YYYY"}
                type="date"
                class="input text"
                @change=${(event) => this.onFormUpdate(value, index, field, event)}
                .value=${this.state.formData[index][field]}
                />
            </div>
        </td>`
    }

    // Left columns for vendor mapping table
    vendorLeftColumns = [
        {
            label: 'VENDOR NAME',
            key: 'vendorName',
            isSortable: true,
            isMain: true,
            hidden: true,  // This column will be hidden via CSS
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'vendorName',  'Select vendor name', 'planIt',) }
        },
        {
            label: 'PLANIT',
            key: 'planIt',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'planIt', 'Select vendor code') }
        },
        {
            label: 'MEDIA TOOLS',
            key: 'mediaTools',
            isSortable: true,
            isMain: true,
            hidden: true,  // This column will be hidden via CSS
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'mediaTools', 'Select vendor code') }
        },
        {
            label: 'MEDIA OCEAN',
            key: 'mediaOcean',
            isSortable: true,
            isMain: true,
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'mediaOcean', 'Select vendor code') }
        },
        {
            label: 'OMNI DICTIONARY',
            key: 'omniDictionary',
            isSortable: true,
            hidden: true,  // This column will be hidden via CSS
            template: (value, index) => { return this.getDropdownTemplate(value, index, 'omniDictionary', 'Select vendor code') }
        }
    ]

    // Right columns for vendor mapping table
    vendorRightColumns = [
        {
            label: 'START DATE',
            key: 'startDate',
            isSortable: true,
            template: (value, index) => { return this.getDateTemplate(value, index, 'startDate') }
        },
        {
            label: 'END DATE',
            key: 'endDate',
            isSortable: true,
            template: (value, index) => { return this.getDateTemplate(value, index, 'endDate') }
        },
        {
            label: 'ACTIONS',
            key: 'actions',
            passthrough: true,
            template: (value, index) => {
                return html`
                <td part="table-body-cell">
                    <div style="display: flex;justify-content: center;">
                            <omni-tooltip-next>
                                <button slot="invoker" aria-haspopup="true" class="icon" ?disabled="${!value.vendorMappingId}" @click=${(e) => { this.deleteRow(value, index) }}>
                                    <omni-icon class="is-size-4" icon-id="omni:interactive:delete"></omni-icon>
                                </button>
                                <div slot="content">Delete mappings</div>
                            </omni-tooltip-next>
                    </div>
                </td>`;
            }
        }
    ]

    /**
     * The below code is creating a table cell (`<td>`) with loading skeleton
     * @returns {table cell}
    */ 
    displaySkeletonTable() {
        let rows = '';
        for (let i = 0; i < 9; i++) {
            rows = rows + '<div className="table skeleton"></div>';
        }
        rows = (html`${rows}`);
        return <>{parse(rows.values[0])}</>;
    }

    // Add this new method before render()
    filterVisibleColumns = (columns) => {
        return columns.filter(col => !col.hidden);
    }

    render() {
        return (<>
            <div className="ratesAndFeesContainer vendorMappingContainer">
                <input type="file" id="vendorMappingInput" accept=".xlsx, .xls" hidden />
                <LoaderComponent spinnerCount={this.state.spinnerCount} displayWholePage={true} />
                    <OmniTileComponent heading="">
                                <OmniToolbarComponent slot="header">
                                    <label slot="start">Vendor mappings</label>
                                    {this.state.formData.length ?
                                        <div slot="end" className="custom-dropdown-menu">
                                                <button className="secondary marginRight20" onClick={this.addMappingRow}>Add Vendor mapping</button>
                                                <OmniIconMenu
                                                    options={this.importExportOption()}
                                                    iconId="omni:interactive:importExport"
                                                    iconClass="is-size-4"
                                                    tooltip="Import/Export"
                                                    onDropdownChange={(event) => this.importExportClick(event)}>
                                                </OmniIconMenu>
                                                <OmniSearchComponent className="searchIcon" onSearchUpdate={this.updateSearch} slot="end" ph="Search"></OmniSearchComponent>
                                                <div className="Line-Copy-2"></div>
                                                <OmniIconComponent className="is-size-4" icon-id="omni:interactive:filter"></OmniIconComponent>
                                        </div> : ''
                                    }
                                </OmniToolbarComponent>
                    </OmniTileComponent>
                    {(!this.state.loaded && this.state.formData.length === 0) && this.displaySkeletonTable()}
                    {this.state.loaded && (this.state.formData.length ? 
                        <OmniTableStickyComponent
                            id="vendorMappingTable"
                            stickyside="right"
                            data={this.state.formData}
                            leftColumns={this.filterVisibleColumns(this.vendorLeftColumns)}
                            rightColumns={this.vendorRightColumns}
                            onSortUpdate={this.onSortUpdate}
                            key={this.state.formData.length}
                            stickyscrollbar={true}
                            keyFn={row => row.vendorMappingId}>
                        </OmniTableStickyComponent> : 
                        <div className='no-placements-content'>
                            <div className="no-data-container">
                                <div className="no-data-message">
                                    <p>{this.state.filtersApplied ?'No Data Found!': 'There are no Vendors mapped yet.'}</p>
                                    <p>{!this.state.filtersApplied ?'Please click “Create vendor mappings” button below to start.' : '' }</p>
                                </div>
                                {!this.state.filtersApplied && <button
                                    className='secondary'
                                    onClick={() => {
                                    this.addMappingRow()}}>Create vendor mappings</button>
                                }
                            </div>
                        </div>)
                    }
                    <div className="footer-buttons">
                        <button className="save-edit-button" disabled={this.state.disableForm} onClick={this.saveEdits}>Save</button>
                        <button className="tertiary" disabled={this.state.disableCancel} onClick={this.cancelEdits}>Cancel</button>
                    </div>
                    <div className={`modal ${this.state.isAlertOpen ? 'is-active' : ''}`}>
                            <div className="modal-background is-warning"></div>
                            <div className="modal-content">
                                <article className="message is-warning">
                                    <div className="message-header">
                                        <p>
                                            <omni-icon icon-id="omni:informative:alert"></omni-icon>
                                            Delete vendor mapping
                                        </p>
                                    </div>
                                    <div className="message-body">
                                        <div>This action would permanently delete the selected mapping.</div>
                                        <div>How would you like to proceed?</div>
                                    </div>
                                    <div className="message-footer">
                                        <div className="field is-grouped is-grouped-right">
                                            <div className="control">
                                                <button
                                                    className="tertiary warning"
                                                    onClick={() => { this.setState({ isAlertOpen: false }) }}>Cancel</button>
                                            </div>
                                            <div className="control">
                                                <button
                                                    className="secondary warning"
                                                    onClick={() => this.confirmDelete()}>Delete</button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            </div>
                    </div>                     
            </div>
        </>)
    }
}